import React, { useState, useRef, useEffect } from 'react';
import { t } from './translations';

// ─── VOICE GLOWS via TTS ─────────────────────────────────────────────────────
// Converts a text glow into spoken audio using the browser's Web Speech API.
// 100% anonymous — no voice recording, sender's voice never used.
// Recipient hears their glow read aloud in a warm, chosen voice character.

// Voice characters — personality mapped to pitch/rate/voice preferences
const VOICE_CHARS = [
  { id:'warm',    label:{ en:'Warm Friend',   ar:'صاحب دافي'  }, pitch:1.0, rate:0.88, icon:'💛' },
  { id:'gentle',  label:{ en:'Gentle Soul',   ar:'روح لطيفة'  }, pitch:1.1, rate:0.82, icon:'🌸' },
  { id:'bright',  label:{ en:'Cheerful Voice',ar:'صوت مبهج'   }, pitch:1.2, rate:0.95, icon:'✨' },
  { id:'calm',    label:{ en:'Calm Presence', ar:'هدوء'       }, pitch:0.95,rate:0.80, icon:'🕊' },
];

// ── Player component — reads a saved message aloud ────────────────────────────
export function VoiceGlowPlayer({ text, emoji, lang }) {
  const T = t[lang];
  const [playing, setPlaying] = useState(false);
  const [selectedChar, setSelectedChar] = useState(VOICE_CHARS[0]);
  const [supported, setSupported] = useState(true);
  const synthRef = useRef(null);
  const utterRef = useRef(null);

  useEffect(() => {
    if (!window.speechSynthesis) setSupported(false);
    return () => { window.speechSynthesis?.cancel(); };
  }, []);

  function play() {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();

    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = lang === 'ar' ? 'ar-SA' : 'en-US';
    utter.pitch = selectedChar.pitch;
    utter.rate = selectedChar.rate;
    utter.volume = 1;

    // Try to find a matching voice
    const voices = window.speechSynthesis.getVoices();
    const match = voices.find(v =>
      v.lang.startsWith(lang === 'ar' ? 'ar' : 'en') && !v.name.toLowerCase().includes('google')
    ) || voices.find(v => v.lang.startsWith(lang === 'ar' ? 'ar' : 'en'));
    if (match) utter.voice = match;

    utter.onstart = () => setPlaying(true);
    utter.onend = () => setPlaying(false);
    utter.onerror = () => setPlaying(false);

    utterRef.current = utter;
    window.speechSynthesis.speak(utter);
    setPlaying(true);
  }

  function stop() {
    window.speechSynthesis?.cancel();
    setPlaying(false);
  }

  if (!supported) return null;

  return (
    <div style={{marginTop:16,padding:'14px 18px',background:'rgba(255,209,102,0.07)',border:'1px solid rgba(255,209,102,0.2)',borderRadius:14}}>
      <div style={{fontSize:'0.72rem',color:'var(--gold)',fontWeight:600,letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:10}}>
        🎙 {lang==='ar'?'اسمع الضو':'Hear this glow'}
      </div>

      {/* Voice character picker */}
      <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
        {VOICE_CHARS.map(c=>(
          <button key={c.id} onClick={()=>setSelectedChar(c)}
            style={{display:'flex',alignItems:'center',gap:5,padding:'5px 10px',borderRadius:100,border:`1.5px solid ${selectedChar.id===c.id?'var(--gold)':'rgba(255,255,255,0.12)'}`,background:selectedChar.id===c.id?'rgba(255,209,102,0.12)':'transparent',color:selectedChar.id===c.id?'var(--gold)':'var(--text-muted)',fontSize:'0.75rem',cursor:'pointer',fontFamily:'inherit',transition:'all 0.15s'}}>
            <span style={{fontSize:'0.85rem'}}>{c.icon}</span> {c.label[lang]}
          </button>
        ))}
      </div>

      {/* Play/Stop */}
      <button
        onClick={playing ? stop : play}
        style={{display:'flex',alignItems:'center',gap:8,padding:'9px 18px',borderRadius:10,background:playing?'rgba(255,107,107,0.15)':'rgba(255,209,102,0.12)',border:`1px solid ${playing?'rgba(255,107,107,0.3)':'rgba(255,209,102,0.25)'}`,color:playing?'#FF8080':'var(--gold)',fontSize:'0.85rem',cursor:'pointer',fontFamily:'inherit',transition:'all 0.2s'}}>
        {playing ? (
          <><span style={{fontSize:'1rem'}}>⏹</span>{lang==='ar'?'وقّف':'Stop'}</>
        ):(
          <><span style={{fontSize:'1rem'}}>▶</span>{lang==='ar'?'استمع':'Listen'}</>
        )}
      </button>
      <p style={{fontSize:'0.7rem',color:'var(--text-muted)',marginTop:8}}>
        {lang==='ar'?'الصوت مولّد بالذكاء الاصطناعي — مجهول تماماً':'AI-generated voice — completely anonymous'}
      </p>
    </div>
  );
}

// ── Sender component — preview TTS before sending ─────────────────────────────
export function VoiceGlowSender({ text, lang, onSendAsVoice }) {
  const T = t[lang];
  const [selectedChar, setSelectedChar] = useState(VOICE_CHARS[0]);
  const [previewing, setPreviewing] = useState(false);
  const [open, setOpen] = useState(false);

  if (!text?.trim()) return null;
  if (!window.speechSynthesis) return null;

  function preview() {
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = lang === 'ar' ? 'ar-SA' : 'en-US';
    utter.pitch = selectedChar.pitch;
    utter.rate = selectedChar.rate;
    const voices = window.speechSynthesis.getVoices();
    const match = voices.find(v => v.lang.startsWith(lang === 'ar' ? 'ar' : 'en'));
    if (match) utter.voice = match;
    utter.onstart = () => setPreviewing(true);
    utter.onend   = () => setPreviewing(false);
    utter.onerror = () => setPreviewing(false);
    window.speechSynthesis.speak(utter);
  }

  if (!open) return (
    <button onClick={()=>setOpen(true)}
      style={{display:'flex',alignItems:'center',gap:6,background:'transparent',border:'1px solid rgba(45,212,191,0.3)',color:'var(--teal)',borderRadius:10,padding:'7px 14px',fontSize:'0.8rem',cursor:'pointer',fontFamily:'inherit',marginTop:8,transition:'all 0.2s'}}>
      🎙 {lang==='ar'?'ابعت كصوت (مجهول)':'Send as voice (anonymous)'}
    </button>
  );

  return (
    <div style={{marginTop:10,padding:'14px 16px',background:'rgba(45,212,191,0.07)',border:'1px solid rgba(45,212,191,0.2)',borderRadius:14}}>
      <div style={{fontSize:'0.72rem',color:'var(--teal)',fontWeight:600,marginBottom:10}}>
        🎙 {lang==='ar'?'اختار صوت — صوتك مش هيتستخدم':'Choose a voice — your real voice is never used'}
      </div>
      <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
        {VOICE_CHARS.map(c=>(
          <button key={c.id} onClick={()=>setSelectedChar(c)}
            style={{display:'flex',alignItems:'center',gap:5,padding:'5px 10px',borderRadius:100,border:`1.5px solid ${selectedChar.id===c.id?'var(--teal)':'rgba(255,255,255,0.12)'}`,background:selectedChar.id===c.id?'rgba(45,212,191,0.12)':'transparent',color:selectedChar.id===c.id?'var(--teal)':'var(--text-muted)',fontSize:'0.75rem',cursor:'pointer',fontFamily:'inherit'}}>
            {c.icon} {c.label[lang]}
          </button>
        ))}
      </div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        <button onClick={preview}
          style={{padding:'7px 14px',borderRadius:9,background:'rgba(45,212,191,0.1)',border:'1px solid rgba(45,212,191,0.25)',color:'var(--teal)',fontSize:'0.8rem',cursor:'pointer',fontFamily:'inherit'}}>
          {previewing?(lang==='ar'?'⏹ وقّف':'⏹ Stop'):(lang==='ar'?'▶ معاينة':'▶ Preview')}
        </button>
        <button onClick={()=>{ window.speechSynthesis?.cancel(); onSendAsVoice?.(selectedChar); setOpen(false); }}
          style={{padding:'7px 14px',borderRadius:9,background:'rgba(45,212,191,0.18)',border:'1px solid rgba(45,212,191,0.35)',color:'var(--teal)',fontSize:'0.8rem',fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
          {lang==='ar'?'ابعت بالصوت ده':'Send with this voice'}
        </button>
        <button onClick={()=>{window.speechSynthesis?.cancel();setOpen(false);}}
          style={{padding:'7px 14px',borderRadius:9,background:'transparent',border:'1px solid var(--border)',color:'var(--text-muted)',fontSize:'0.8rem',cursor:'pointer',fontFamily:'inherit'}}>
          {lang==='ar'?'إلغاء':'Cancel'}
        </button>
      </div>
    </div>
  );
}
